import React, { useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';

const AdminServices = () => {
  const { toast } = useToast();
  const [services, setServices] = useState([
    {
      id: 1,
      title: 'Floor Tiling',
      description: 'Professional floor tiling for residential and commercial spaces with precision installation.',
      icon: '🔲',
    },
    {
      id: 2,
      title: 'Wall Tiling',
      description: 'Transform your walls with expert tiling solutions for lasting beauty and durability.',
      icon: '📐',
    },
    {
      id: 3,
      title: 'Bathroom Tiling',
      description: 'Complete bathroom tiling services including waterproofing and modern designs.',
      icon: '🚿',
    },
    {
      id: 4,
      title: 'Kitchen Tiling',
      description: 'Beautiful kitchen backsplashes and floor tiling that combines style with function.',
      icon: '🏠',
    },
    {
      id: 5,
      title: 'Outdoor Tiling',
      description: 'Weather-resistant outdoor tiling for patios, balconies, and pool areas.',
      icon: '🌳',
    },
    {
      id: 6,
      title: 'Commercial Tiling',
      description: 'Large-scale commercial projects with efficient timelines and quality results.',
      icon: '🏢',
    },
  ]);

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', description: '', icon: '' });
  const [isAdding, setIsAdding] = useState(false);
  const [newService, setNewService] = useState({ title: '', description: '', icon: '' });

  const handleEdit = (service) => {
    setEditingId(service.id);
    setEditForm({ title: service.title, description: service.description, icon: service.icon });
  };

  const handleSave = () => {
    setServices(services.map(s => 
      s.id === editingId 
        ? { ...s, title: editForm.title, description: editForm.description, icon: editForm.icon }
        : s
    ));
    setEditingId(null);
    toast({
      title: 'Service Updated',
      description: 'Changes saved successfully.',
    });
  };

  const handleDelete = (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    
    setServices(services.filter(s => s.id !== id));
    toast({
      title: 'Service Deleted',
      description: 'The service has been removed.',
    });
  };

  const handleAddService = () => {
    if (!newService.title || !newService.description) {
      toast({
        title: 'Error',
        description: 'Please fill in all fields',
        variant: 'destructive',
      });
      return;
    }

    const service = {
      id: Math.max(...services.map(s => s.id)) + 1,
      ...newService,
      icon: newService.icon || '⭐',
    };

    setServices([...services, service]);
    setNewService({ title: '', description: '', icon: '' });
    setIsAdding(false);
    toast({
      title: 'Service Added',
      description: 'New service created successfully.',
    });
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Manage Services</h2>
          <button
            onClick={() => setIsAdding(true)}
            className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add New Service
          </button>
        </div>

        {/* Add New Service Form */}
        {isAdding && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Add New Service</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Service Title</label>
                <Input
                  value={newService.title}
                  onChange={(e) => setNewService({ ...newService, title: e.target.value })}
                  placeholder="e.g., Premium Tiling"
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <Textarea
                  value={newService.description}
                  onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                  placeholder="Describe the service..."
                  rows={3}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Icon (emoji)</label>
                <Input
                  value={newService.icon}
                  onChange={(e) => setNewService({ ...newService, icon: e.target.value })}
                  placeholder="⭐"
                  className="w-20"
                />
              </div>
              <div className="flex gap-3">
                <Button onClick={handleAddService} className="bg-blue-600 hover:bg-blue-700">
                  <Save className="w-4 h-4 mr-2" />
                  Save Service
                </Button>
                <Button
                  onClick={() => setIsAdding(false)}
                  variant="outline"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Services List */}
        <div className="grid grid-cols-1 gap-4">
          {services.map((service) => (
            <div key={service.id} className="bg-white rounded-xl shadow-sm p-6">
              {editingId === service.id ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                    <Input
                      value={editForm.title}
                      onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <Textarea
                      value={editForm.description}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      rows={3}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
                    <Input
                      value={editForm.icon}
                      onChange={(e) => setEditForm({ ...editForm, icon: e.target.value })}
                      className="w-20"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={handleSave}
                      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      <Save className="w-4 h-4" />
                      Save
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      className="flex items-center gap-2 bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="text-4xl">{service.icon}</div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                      <p className="text-gray-600">{service.description}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(service)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(service.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminServices;
